/**
 * Record-access dimension filter (DSCO-5543 — Node Based Access Control).
 *
 * Was `DiscoTeamSelector`, hardwired to teams end to end: a `teams` option
 * source, a `teams` URL param, and a "Teams" trigger. Node-based access control
 * makes teams one dimension among several, so every one of those is now read
 * off `/me.accessControl.dimensions[0]` (HLD v0.3.1 §B).
 *
 * Nothing about the dimension is hardcoded here and nothing is looked up:
 * `activeDimension.name` *is* the search entity, the URL param key, and — once
 * capitalized — the displayed label.
 *
 * Three outcomes, in gate order: nothing at all (no dimension / still loading /
 * the entity opted out), the zero-reach notice (a scoped actor with an empty
 * curated option list — HLD §A), or the picker.
 *
 * The selection semantics themselves — URL read, draft, commit, and the storage
 * mirror — live in `useDimensionSelection` (DP-3188), not here: the first-login
 * picker commits the same way, and two copies of "what does committing mean"
 * is exactly the divergence that hook exists to prevent. This component owns
 * only the gates, the label, and the empty-draft rule below.
 */

import { useCallback } from 'react';
import { MultiSelectPopover } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { Users } from 'lucide-react';

import { useAccessDimensionOptions } from '../../api/endpoints/useAccessDimensionOptions';
import { useCurrentUser } from '../../api/endpoints/useCurrentUser';
import { useEntityMetadata } from '../../api/endpoints/useEntityMetadata';
import { useClientId } from '../../hooks/useClientId';
import { useCurrentEntityName } from '../../hooks/useCurrentEntityName';
import { useCurrentUsecase } from '../../hooks/useCurrentUsecase';
import { useDimensionSelection } from '../../hooks/useDimensionSelection';

type RecordAccessSelectorProps = Readonly<{ collapsed: boolean }>;

/**
 * The dimension's display label, derived from its `name` — the BE transmits no
 * label (HLD v0.3.1 §A). Backend-driven data, so it is deliberately not
 * `t()`-wrapped (see the repo i18n convention); where it appears inside FE copy
 * it is interpolated into a `t()` sentence, never concatenated onto one.
 */
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Trigger styling for the zero-reach notice. Deliberately the same geometry and
 * tokens as disco-ui's `MultiSelectPopover` trigger (`h-9`/`size-7`,
 * `rounded-lg`, `bg-background`) so the sidebar header does not reflow between
 * a reachable and an unreachable dimension — it reads as the same control,
 * disabled, rather than as a different widget.
 */
const ZERO_REACH_EXPANDED_CLASS =
  'flex h-9 w-full cursor-not-allowed items-center gap-2.5 rounded-lg border border-input bg-background px-2 text-left opacity-60 outline-none';
const ZERO_REACH_COLLAPSED_CLASS =
  'flex size-7 cursor-not-allowed items-center justify-center rounded-lg bg-background opacity-60 outline-none';

/**
 * Zero reach: the actor's curated `/search` came back empty *and* the dimension
 * grants no blanket access, so there is genuinely nothing to filter by. The HLD
 * (v0.3.1 §A, "Zero-reach state") requires an explicit sentence here — falling
 * through to `null` leaves the grid's generic "No items found" as the only
 * signal, which reads as "no data" when the truth is "no access".
 *
 * Rendered as a natively `disabled` button, which is also why there is no
 * popover: nothing is selectable, so no option list — and therefore no empty
 * checkbox list — can ever mount. The copy takes the trigger's label slot when
 * expanded and the accessible name when collapsed, so the same sentence is the
 * only thing this state ever says, in either variant.
 */
function ZeroReachNotice({
  message,
  collapsed,
}: Readonly<{ message: string; collapsed: boolean }>) {
  return (
    // The hover text lives on the wrapper, not the button: a `disabled` button
    // suppresses pointer events in several engines, so its own `title` may
    // never surface. It matters in both variants — collapsed hides the copy
    // visually, expanded truncates it in a narrow rail.
    <span
      title={message}
      className={collapsed ? 'inline-flex' : 'block w-full'}
    >
      <button
        type="button"
        disabled
        data-testid="record-access-zero-reach"
        className={
          collapsed ? ZERO_REACH_COLLAPSED_CLASS : ZERO_REACH_EXPANDED_CLASS
        }
      >
        <span
          aria-hidden
          className="flex shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4"
        >
          <Users />
        </span>
        <span
          data-testid="record-access-zero-reach-message"
          className={
            collapsed
              ? 'sr-only'
              : 'min-w-0 flex-1 truncate text-sm text-muted-foreground'
          }
        >
          {message}
        </span>
      </button>
    </span>
  );
}

export default function RecordAccessSelector({
  collapsed,
}: RecordAccessSelectorProps) {
  const clientId = useClientId();
  const { activeDimension, userId } = useCurrentUser(clientId);
  const { t } = useTranslation();

  // Empty until `/me` resolves, and empty for good when enforcement is
  // disabled (`dimensions: []`). Both keep the options query disabled, and both
  // render nothing — the guard below never dereferences a null dimension.
  const dimensionName = activeDimension?.name ?? '';

  // The dimension's `name` doubles as the search entity name, so one hook
  // serves every dimension. Rows are curated server-side to the actor's reach.
  const { data: options, isLoading } = useAccessDimensionOptions(
    clientId,
    dimensionName
  );

  // Some entities opt out of the record-access filter via their metadata —
  // notably the dimension's own source entity, which is never self-scoped.
  // The query is disabled for non-entity routes (empty entityName), so
  // metadataResponse is undefined there and the filter shows by default.
  // On a usecase page (DP-3570) the selector reads the same `(entity, usecase)`
  // document the grid reads, so the opt-out reflects the usecase config and
  // the query key never collides with the default page's cached metadata.
  const entityName = useCurrentEntityName();
  const usecase = useCurrentUsecase();
  const { data: metadataResponse } = useEntityMetadata(clientId, entityName, {
    usecase,
  });
  // Both keys, new one first: `applyTeamFilter` is the deprecated alias and is
  // still the only key deployed backends send (HLD §B grants it a one-release
  // window). Reading only the new key made the selector show on entities that
  // had explicitly opted out of record-access filtering.
  const recordAccessGridView = metadataResponse?.data?.frontend?.gridView;
  const showRecordAccessFilter =
    (recordAccessGridView?.applyRecordAccessFilter ??
      recordAccessGridView?.applyTeamFilter) !== false;

  // URL read, draft, and commit (URL + storage mirror, in one go) all come from
  // the shared hook. Called above the gates below because hooks must be
  // unconditional — it takes a nullable `activeDimension` for exactly that
  // reason and degrades to a no-op until `/me` resolves.
  //
  // `userId` is the personnel entity id off `/me`, which is what the storage
  // key demands; empty until the response lands, and the hook skips the mirror
  // rather than writing a key that would collapse distinct users onto one entry.
  // It comes from `useCurrentUser`'s accessor, which knows the id lives at
  // `data.entityData.id` — there is no `data.id` to read, and reaching in by
  // hand is how this shipped a storage key that silently never wrote.
  const { draftSelected, setDraft, commit } = useDimensionSelection({
    options,
    activeDimension,
    clientId,
    userId,
  });

  const handleChange = useCallback(
    (next: Set<string>) => {
      // An empty draft is held in local state only — never committed, so
      // neither the URL nor storage records zero-selected. disco-ui shows the
      // error border + shakes on dismiss attempt; once the user picks at least
      // one, the next change commits and the draft re-syncs.
      if (next.size === 0) {
        setDraft(next);
        return;
      }
      commit(next);
    },
    [commit, setDraft]
  );

  if (!activeDimension || isLoading || !showRecordAccessFilter) {
    return null;
  }

  // The dimension's display label — capitalized and pluralized for the
  // trigger/sentence copy (disco-ui's `MultiSelectPopover` interpolates it
  // lowercased into every built-in string and documents the contract as
  // "pass it in the plural" — e.g. 'Teams', not 'Team'). This is distinct
  // from `dimensionName` above, which stays singular verbatim for the URL
  // param and the search-entity name. One derivation, so trigger and
  // sentence can never disagree with each other.
  const label = `${capitalize(activeDimension.name)}s`;

  if (options.length === 0) {
    // `allAccess` actors are unfiltered by construction, so an empty option
    // list is not a reach problem for them and the notice would be a lie —
    // they simply get no selector. Only a scoped actor is truly zero-reach.
    return activeDimension.allAccess ? null : (
      <ZeroReachNotice
        collapsed={collapsed}
        message={t(
          'dashboard.recordAccessSelector.zeroReach',
          "You don't have access to any {{label}} yet — contact your admin",
          { label: label.toLowerCase() }
        )}
      />
    );
  }

  return (
    <MultiSelectPopover
      // `AccessDimensionOption` and disco-ui's `SelectOption` are the same
      // `{ id, label, color? }` row — the BE `/search` projection feeds the
      // picker with no remapping.
      items={options}
      label={label}
      selected={draftSelected}
      onChange={handleChange}
      variant={collapsed ? 'collapsed' : 'expanded'}
      // Require at least one value. The disco-ui `MultiSelectPopover`
      // shakes + paints an errored border + blocks close when the user
      // tries to dismiss with zero selected, surfacing the validation
      // in-place. The empty-state itself is allowed transiently (held in
      // local draft state) so this UX has a chance to fire.
      minSelected={1}
      // No clear affordance. It contradicts `minSelected={1}`: a control whose
      // only job is to empty the selection cannot be right on a field that
      // refuses to be empty. Worse, it bypassed the commit path — the trigger
      // read "No teams" while the URL, storage and grid stayed scoped, so the
      // sidebar claimed a scope the data did not have. Changing scope goes
      // through the popover, which commits on close.
      clearable={false}
    />
  );
}
